<template>
    <div class="m-profile-page">
      <div class="sticky-header">
        <FeedHeader />
      </div>
  
      <main class="m-profile-content">
        <button class="back-btn" @click="$router.back()">
          ← Назад
        </button>
  
        <div v-if="isLoadingProfile" class="resume-skeleton">
          <div class="skeleton title-skeleton"></div>
          <div class="skeleton card-skeleton"></div>
          <div class="skeleton button-skeleton"></div>
          <div class="skeleton title-skeleton small"></div>
          <div class="skeleton card-skeleton short"></div>
          <div class="skeleton button-skeleton"></div>
        </div>
  
        <div v-else class="resume-page">
          <section class="section-block">
            <h2 class="section-title">Карьера</h2>
  
            <template v-if="career.length">
              <ResumeCard
                v-for="item in career"
                :key="item.id"
                :title="item.company || 'Без названия'"
                :subtitle="item.specialty || 'Специальность не указана'"
                :period="formatCareerPeriod(item)"
                @delete="deleteCareer(item.id)"
              />
            </template>
  
            <div v-else class="empty-state">
              <span>Карьера пока не добавлена</span>
            </div>
  
            <AddAction label="Добавить карьеру" @click="addCareer" />
          </section>
  
          <section class="section-block">
            <h2 class="section-title">Образование</h2>
  
            <template v-if="education.length">
              <ResumeCard
                v-for="item in education"
                :key="item.id"
                :title="item.university || 'Учебное заведение'"
                :subtitle="item.faculty || 'Факультет не указан'"
                :period="formatEducationPeriod(item)"
                @delete="deleteEducation(item.id)"
              />
            </template>
  
            <div v-else class="empty-state">
              <span>Образование пока не добавлено</span>
            </div>
  
            <AddAction label="Добавить образование" @click="addEducation" />
          </section>
  
          <section class="section-block">
            <h2 class="section-title">Языки</h2>
  
            <template v-if="languages.length">
              <ResumeCard
                v-for="lang in languages"
                :key="lang.id"
                :title="lang.language || 'Язык'"
                :subtitle="lang.language_level || 'Уровень не указан'"
                :noPeriod="true"
                @delete="deleteLanguage(lang.id)"
              />
            </template>
  
            <div v-else class="empty-state">
              <span>Языки пока не добавлены</span>
            </div>
  
            <AddAction label="Добавить язык" @click="addLanguage" />
          </section>
        </div>
      </main>
  
      <div v-if="isDeleting" class="overlay-loader">
        <div class="spinner"></div>
      </div>
  
      <ProfileCareerDrawer
        v-if="showCareerDrawer"
        :visible="showCareerDrawer"
        @close="showCareerDrawer = false"
        @save="onCareerSave"
        @snackbar="handleSnackbar"
      />
  
      <ProfileEducationDrawer
        :visible="showEducationDrawer"
        @close="showEducationDrawer = false"
        @save="onEducationSave"
        @snackbar="handleSnackbar"
      />
  
      <ProfileLanguageDrawer
        :visible="showLanguageDrawer"
        @close="showLanguageDrawer = false"
        @save="onLanguageSave"
        @snackbar="handleSnackbar"
      />
  
      <Snackbar
        :message="snackbarMessage"
        :type="snackbarType"
        :show="showSnackbar"
      />
  
      <MobileTabBar />
    </div>
  </template>
  
  <script setup>
  import { onBeforeUnmount, onMounted, ref } from 'vue';
  import FeedHeader from '../FeedHeader.vue';
  import MobileTabBar from '../MobileTabBar2.vue';
  import UserService from '../../api/UserService';
  import SummaryService from '../../api/SummaryService';
  import ResumeCard from '../profile/ResumeCard.vue';
  import AddAction from '../profile/AddAction.vue';
  import ProfileCareerDrawer from '../profile/ProfileCareerDrawer.vue';
  import ProfileEducationDrawer from '../profile/ProfileEducationDrawer.vue';
  import ProfileLanguageDrawer from '../profile/ProfileLanguageDrawer.vue';
  import Snackbar from '../Snackbar.vue';
  
  const userId = localStorage.getItem('user_id');
  
  const user = ref({});
  const career = ref([]);
  const education = ref([]);
  const languages = ref([]);
  
  const isLoadingProfile = ref(true);
  const isDeleting = ref(false);
  
  const showCareerDrawer = ref(false);
  const showEducationDrawer = ref(false);
  const showLanguageDrawer = ref(false);
  
  const showSnackbar = ref(false);
  const snackbarMessage = ref('');
  const snackbarType = ref('success');
  
  let snackbarTimer = null;
  
  function handleSnackbar(message, type = 'success') {
    snackbarMessage.value = message;
    snackbarType.value = type;
    showSnackbar.value = true;
  
    clearTimeout(snackbarTimer);
    snackbarTimer = setTimeout(() => {
      showSnackbar.value = false;
    }, 3000);
  }
  
  function getLocalProfile() {
    try {
      return JSON.parse(localStorage.getItem('user_profile')) || {};
    } catch {
      return {};
    }
  }
  
  function syncResumeFromProfile(profile) {
    career.value = Array.isArray(profile?.career) ? [...profile.career] : [];
    education.value = Array.isArray(profile?.education) ? [...profile.education] : [];
    languages.value = Array.isArray(profile?.languages) ? [...profile.languages] : [];
  }
  
  function setUser(profile) {
    user.value = profile || {};
    syncResumeFromProfile(user.value);
    localStorage.setItem('user_profile', JSON.stringify(user.value));
  }
  
  function updateUserCache(patch) {
    user.value = {
      ...user.value,
      ...patch,
    };
  
    localStorage.setItem('user_profile', JSON.stringify(user.value));
  }
  
  async function loadProfile() {
    const startedAt = performance.now();
    isLoadingProfile.value = true;
  
    const cached = getLocalProfile();
  
    if (cached && Object.keys(cached).length) {
      user.value = cached;
      syncResumeFromProfile(cached);
    }
  
    try {
      const fetched = await UserService.getProfile(userId);
      setUser(fetched || {});
    } catch (error) {
      console.error('❌ Ошибка при загрузке резюме:', error);
      handleSnackbar('Не удалось загрузить резюме', 'error');
    } finally {
      const minSkeletonMs = 400;
      const elapsed = performance.now() - startedAt;
      const delay = Math.max(0, minSkeletonMs - elapsed);
  
      setTimeout(() => {
        isLoadingProfile.value = false;
      }, delay);
    }
  }
  
  function formatCareerPeriod(item) {
    const start = item?.start_date || 'Начало не указано';
    const end = item?.end_date || 'по настоящее время';
    return `${start} – ${end}`;
  }
  
  function formatEducationPeriod(item) {
    return item?.end_date ? `Окончил в ${item.end_date}` : 'Год окончания не указан';
  }
  
  async function deleteCareer(id) {
    if (!id) return;
  
    isDeleting.value = true;
  
    try {
      await SummaryService.deleteCareer(id);
      career.value = career.value.filter((item) => item.id !== id);
      updateUserCache({ career: [...career.value] });
      handleSnackbar('Карьера удалена');
    } catch (error) {
      console.error('❌ Ошибка при удалении карьеры:', error);
      handleSnackbar('Не удалось удалить карьеру', 'error');
    } finally {
      isDeleting.value = false;
    }
  }
  
  async function deleteEducation(id) {
    if (!id) return;
  
    isDeleting.value = true;
  
    try {
      await SummaryService.deleteEducation(id);
      education.value = education.value.filter((item) => item.id !== id);
      updateUserCache({ education: [...education.value] });
      handleSnackbar('Образование удалено');
    } catch (error) {
      console.error('❌ Ошибка при удалении образования:', error);
      handleSnackbar('Не удалось удалить образование', 'error');
    } finally {
      isDeleting.value = false;
    }
  }
  
  async function deleteLanguage(id) {
    if (!userId || !id) return;
  
    isDeleting.value = true;
  
    try {
      await SummaryService.deleteLanguage(userId, id);
      languages.value = languages.value.filter((item) => item.id !== id);
      updateUserCache({ languages: [...languages.value] });
      handleSnackbar('Язык удалён');
    } catch (error) {
      console.error('❌ Ошибка при удалении языка:', error);
      handleSnackbar('Не удалось удалить язык', 'error');
    } finally {
      isDeleting.value = false;
    }
  }
  
  function addCareer() {
    showCareerDrawer.value = true;
  }
  
  function addEducation() {
    showEducationDrawer.value = true;
  }
  
  function addLanguage() {
    showLanguageDrawer.value = true;
  }
  
  async function onCareerSave() {
    showCareerDrawer.value = false;
    await loadProfile();
    handleSnackbar('Карьера сохранена');
  }
  
  async function onEducationSave() {
    showEducationDrawer.value = false;
    await loadProfile();
    handleSnackbar('Образование сохранено');
  }
  
  async function onLanguageSave() {
    showLanguageDrawer.value = false;
    await loadProfile();
    handleSnackbar('Язык сохранён');
  }
  
  onMounted(() => {
    loadProfile();
  });
  
  onBeforeUnmount(() => {
    clearTimeout(snackbarTimer);
  });
  </script>
  
  <style scoped>
  .m-profile-page {
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    background: var(--background-color);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  
  .sticky-header {
    flex-shrink: 0;
  }
  
  .m-profile-content {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 16px;
    padding-bottom: 96px;
    box-sizing: border-box;
    -webkit-overflow-scrolling: touch;
  }
  
  .back-btn {
    height: 36px;
    padding: 0 14px;
    margin-bottom: 14px;
    border: none;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(8px);
    color: #111;
    font-size: 13px;
    font-family: 'Eastman-Medium', sans-serif;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  }
  
  [data-theme='dark'] .back-btn {
    background: rgba(40, 40, 40, 0.92);
    color: #fff;
  }
  
  .resume-page {
    width: 100%;
    max-width: 430px;
    margin: 0 auto;
    padding: 20px;
    box-sizing: border-box;
    border-radius: 24px;
    background: var(--sidebar-background);
    border: 1px solid #e6e6e6;
    font-family: 'Eastman-Regular', sans-serif;
  }
  
  [data-theme='dark'] .resume-page {
    background: #2f2f2f;
    color: #eee;
    border-color: #3f3f3f;
  }
  
  .section-block {
    margin-bottom: 34px;
  }
  
  .section-block:last-child {
    margin-bottom: 0;
  }
  
  .section-title {
    margin: 0 0 14px;
    font-family: 'Eastman-Bold', sans-serif;
    font-size: 18px;
    line-height: 1.2;
    letter-spacing: -0.4px;
    color: var(--text-primary);
  }
  
  .empty-state {
    width: 100%;
    box-sizing: border-box;
    padding: 18px 16px;
    margin-bottom: 12px;
    border-radius: 18px;
    background: var(--input-background);
    border: 1px dashed #d8d8d8;
    color: var(--text-secondary);
    font-size: 13px;
    font-family: 'Eastman-Medium', sans-serif;
    text-align: center;
  }
  
  [data-theme='dark'] .empty-state {
    border-color: #555;
  }
  
  .overlay-loader {
    position: fixed;
    inset: 0;
    z-index: 9999;
    width: 100vw;
    height: 100vh;
    background: rgba(255, 255, 255, 0.75);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  [data-theme='dark'] .overlay-loader {
    background: rgba(0, 0, 0, 0.6);
  }
  
  .spinner {
    width: 42px;
    height: 42px;
    border: 5px solid #ccc;
    border-top-color: #2173e5;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  
  .resume-skeleton {
    width: 100%;
    max-width: 430px;
    margin: 0 auto;
    padding: 20px;
    box-sizing: border-box;
    border-radius: 24px;
    background: var(--sidebar-background);
  }
  
  .skeleton {
    background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
    background-size: 200% 100%;
    animation: skeleton-shimmer 1.2s infinite;
    border-radius: 14px;
  }
  
  [data-theme='dark'] .skeleton {
    background: linear-gradient(90deg, #333 25%, #444 50%, #333 75%);
    background-size: 200% 100%;
  }
  
  .title-skeleton {
    width: 42%;
    height: 24px;
  }
  
  .title-skeleton.small {
    width: 54%;
    margin-top: 28px;
  }
  
  .card-skeleton {
    width: 100%;
    height: 98px;
    margin-top: 16px;
  }
  
  .card-skeleton.short {
    height: 78px;
  }
  
  .button-skeleton {
    width: 62%;
    height: 42px;
    margin-top: 14px;
    border-radius: 999px;
  }
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
  
  @keyframes skeleton-shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
  
  @media (min-width: 768px) {
    .m-profile-page {
      max-width: 430px;
      margin: 0 auto;
      border-left: 1px solid var(--separator_home);
      border-right: 1px solid var(--separator_home);
    }
  }
  
  @media (max-width: 360px) {
    .m-profile-content {
      padding-left: 12px;
      padding-right: 12px;
    }
  
    .resume-page,
    .resume-skeleton {
      padding: 16px;
    }
  
    .section-title {
      font-size: 17px;
    }
  }
  </style>
  